import {Flex, HStack, Icon, Text, VStack} from '@chakra-ui/react';
import {Link} from 'react-router-dom';
import {routes} from 'ff-common';
import {useTranslation} from 'react-i18next';
import {IconType} from 'react-icons';
import {
  RiArrowRightLine,
  RiLeafLine,
  RiListUnordered,
  RiLoopRightLine,
} from 'react-icons/ri';
import {useClientRoute} from '@/hooks/auth/useClientRoute';

/**
 * Les cartes d'action de l'interface client : Mon cadencier, Mes listes, Mes produits par saison.
 * Le bloc d'accueil affiche les trois : en colonnes dans la carte d'accueil en version ordinateur,
 * empilées sous la recherche en version mobile. La page panier vide reprend Mon cadencier et
 * Mes listes.
 */

// colonne : pictogramme et titre, sous-titre dessous. ligne : tout sur une rangée.
type Variante = 'colonne' | 'ligne';

const CarteAction: React.FC<{
  variante: Variante;
  route: string;
  icone: IconType;
  couleurIcone: string;
  fondIcone: string;
  titre: string;
  sousTitre: string;
}> = ({variante, route, icone, couleurIcone, fondIcone, titre, sousTitre}) => {
  const pictogramme = (
    <Flex
      aria-hidden
      flexShrink={0}
      boxSize={variante === 'colonne' ? '34px' : '36px'}
      borderRadius={variante === 'colonne' ? '10px' : '12px'}
      bg={fondIcone}
      alignItems='center'
      justifyContent='center'
    >
      <Icon
        as={icone}
        boxSize='18px'
        color={couleurIcone}
      />
    </Flex>
  );
  const fleche = (
    <Icon
      as={RiArrowRightLine}
      aria-hidden
      flexShrink={0}
      boxSize='15px'
      color='gray.text_secondary'
    />
  );

  if (variante === 'ligne') {
    return (
      <HStack
        as={Link}
        to={route}
        spacing='12px'
        py='15.5px'
        px='14px'
        borderRadius='12px'
        bg='gray.card'
        transition='opacity 0.2s ease'
        _hover={{opacity: 0.8}}
      >
        {pictogramme}
        <VStack
          flex='1'
          minW={0}
          align='start'
          spacing='2px'
        >
          <Text
            fontWeight='700'
            fontSize='15px'
            lineHeight='19px'
            color='blue.navy'
          >
            {titre}
          </Text>
          <Text
            fontSize='xs'
            lineHeight='15px'
            color='gray.text_secondary'
          >
            {sousTitre}
          </Text>
        </VStack>
        {fleche}
      </HStack>
    );
  }
  return (
    <VStack
      as={Link}
      to={route}
      flex='1'
      minW={0}
      align='stretch'
      spacing='11px'
      p='12px'
      borderRadius='12px'
      bg='gray.card_warm'
      transition='opacity 0.2s ease'
      _hover={{opacity: 0.8}}
    >
      <HStack spacing='12px'>
        {pictogramme}
        <Text
          flex='1'
          fontWeight='600'
          fontSize='md'
          lineHeight='18px'
          color='blue.navy'
        >
          {titre}
        </Text>
        {fleche}
      </HStack>
      <Text
        fontSize='xs'
        lineHeight='18px'
        color='gray.text_secondary'
      >
        {sousTitre}
      </Text>
    </VStack>
  );
};

export const CarteCadencier: React.FC<{variante: Variante}> = ({variante}) => {
  const {t} = useTranslation();
  const {buildClientRoute} = useClientRoute();

  return (
    <CarteAction
      variante={variante}
      route={buildClientRoute(routes.client.cadencier)}
      icone={RiLoopRightLine}
      couleurIcone='blue.main'
      fondIcone='blue.main_light_bg'
      titre={t('global.mon_cadencier')}
      sousTitre={t('accueil.cartes.cadencier.sous_titre')}
    />
  );
};

export const CarteListes: React.FC<{variante: Variante}> = ({variante}) => {
  const {t} = useTranslation();
  const {buildClientRoute} = useClientRoute();

  return (
    <CarteAction
      variante={variante}
      route={buildClientRoute(routes.client.listes_default)}
      icone={RiListUnordered}
      couleurIcone='green.success'
      fondIcone='green.pastel_bg'
      titre={t('liste.mes_listes')}
      sousTitre={t('accueil.cartes.listes.sous_titre')}
    />
  );
};

const CarteSaison: React.FC<{variante: Variante}> = ({variante}) => {
  const {t} = useTranslation();
  const {buildClientRoute} = useClientRoute();

  return (
    <CarteAction
      variante={variante}
      route={buildClientRoute(routes.client.mes_produits_par_saison)}
      icone={RiLeafLine}
      couleurIcone='orange.warning'
      fondIcone='orange.warning_50'
      titre={t('liste.produits_par_saison')}
      sousTitre={t('accueil.cartes.saison.sous_titre')}
    />
  );
};

export const AccueilCartes: React.FC<{variante: Variante}> = ({variante}) => {
  const cartes = (
    <>
      <CarteCadencier variante={variante} />
      <CarteListes variante={variante} />
      <CarteSaison variante={variante} />
    </>
  );

  if (variante === 'ligne') {
    return (
      <VStack
        align='stretch'
        spacing='8px'
      >
        {cartes}
      </VStack>
    );
  }
  return (
    <HStack
      align='stretch'
      spacing='12px'
    >
      {cartes}
    </HStack>
  );
};
